import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { signal } from '@angular/core';
import { Title } from '@angular/platform-browser';

interface RouteHeader {
  title: string;
  subtitle: string | null;
}

/**
 * Service for managing and tracking page titles based on route changes.
 * Uses Angular's route.title property to dynamically update the current page title.
 */
@Injectable({
  providedIn: 'root',
})
export class TitleService {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private documentTitle = inject(Title);

  pageTitle = signal<string>('Dashboard');
  pageSubtitle = signal<string | null>(null);

  constructor() {
    this.initializeTitleTracking();
  }

  /**
   * Initialize title tracking for route navigation changes
   */
  private initializeTitleTracking(): void {
    // Listen to route changes and extract title from route data
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.getRouteHeader())
      )
      .subscribe((header) => {
        this.pageTitle.set(header.title);
        this.pageSubtitle.set(header.subtitle);
        this.documentTitle.setTitle(`${header.title} | Kenergy Hub`);
      });

    // Don't set initial title synchronously - wait for first navigation
  }

  /**
   * Extract the page title from the current route's title property
   * Traverses nested routes to find the deepest route with a title
   */
  private getRouteHeader(): RouteHeader {
    try {
      let route: ActivatedRoute | null = this.activatedRoute.root;
      let header: RouteHeader = { title: 'Dashboard', subtitle: null };

      while (route) {
        const data = route?.snapshot?.data;
        if (data?.['title']) {
          header = {
            title: data['title'],
            subtitle: data['subtitle'] ?? null,
          };
        }

        // Check children
        const children: ActivatedRoute[] = route?.children || [];
        if (children.length === 0) {
          break;
        }

        route = children[0];
      }

      return header;
    } catch (error) {
      return { title: 'Dashboard', subtitle: null };
    }
  }
}
